import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { ConvexError } from "convex/values";
import React, { Fragment, useCallback, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { Text, View } from "react-native";
import Toast from "react-native-toast-message";
import BasicInfoIcon from "@/assets/svgs/basic-info-icon";
import CloseIcon from "@/assets/svgs/close-icon";
import ContactIcon from "@/assets/svgs/contact-icon";
import LearningPreferencesIcon from "@/assets/svgs/learning-preferences-icon";
import SendMessageIcon from "@/assets/svgs/send-message-icon";
import {
  type ChildFormInput,
  useChildForm,
} from "@/elements/hooks/use-child-form";
import { useSheet } from "../shared/sheet-provider";
import type {
  MultiSelectSheetPayload,
  MultiSelectSheetResult,
} from "../sheets/multi-select-sheet";
import type {
  SingleSelectSheetPayload,
  SingleSelectSheetResult,
} from "../sheets/single-select-sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { CheckboxInput } from "../ui/checkbox-input";
import { Input } from "../ui/input";
import { Select } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { handleAuthError } from "../utils/auth-error-handler";
import { api } from "../utils/convex-api";
import { contactSupport } from "../utils/helpers";
import { SheetIds } from "../utils/sheet-ids";
import { isIbCurriculumLabel } from "../utils/curriculum";
import SubjectQualificationsForm from "./subjects-qualification-form";

function ChildForm({
  createMode,
  child,
  setSelectedChildId,
  setCreateMode,
  onScrollToTop,
}: {
  createMode: boolean;
  child: typeof api.parent.manageChildren.queries.getChild._returnType | null;
  setSelectedChildId: (studentId: Id<"students"> | null) => void;
  setCreateMode: (createMode: boolean) => void;
  onScrollToTop: () => void;
}) {
  const {
    control,
    watch,
    setValue,
    setError,
    togglePreference,
    formState: { isValid },
    handleSubmit,
  } = useChildForm({ initialData: child });

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { open: openSingleSelectSheet } = useSheet<
    SingleSelectSheetPayload,
    SingleSelectSheetResult
  >(SheetIds.SINGLE_SELECT);
  const { open: openMultiSelectSheet } = useSheet<
    MultiSelectSheetPayload,
    MultiSelectSheetResult
  >(SheetIds.MULTI_SELECT);

  const { data: curriculums = [] } = useQuery({
    ...convexQuery(
      api.parent.manageChildren.queries.getAvailableCurriculums,
      {},
    ),
  });

  const selectedCurriculumId = watch("curriculumId") as Id<"curriculum"> | "";
  const selectedProgrammeId = watch("programmeId") as Id<"keyStages"> | "";
  const selectedYearGroupId = watch("yearGroupId") as Id<"yearGroups"> | "";

  const selectedCurriculum = useMemo(
    () =>
      curriculums.find(
        (curriculum) => curriculum.value === selectedCurriculumId,
      ),
    [curriculums, selectedCurriculumId],
  );

  const isIbCurriculum = useMemo(
    () => isIbCurriculumLabel(selectedCurriculum?.label),
    [selectedCurriculum],
  );

  const { data: programmes = [] } = useQuery(
    convexQuery(
      api.parent.manageChildren.queries.getProgrammesForCurriculum,
      isIbCurriculum && selectedCurriculumId
        ? { curriculumId: selectedCurriculumId as Id<"curriculum"> }
        : "skip",
    ),
  );

  const yearGroupsArgs = useMemo(() => {
    if (isIbCurriculum) {
      if (!selectedProgrammeId) return "skip" as const;
      return { programmeId: selectedProgrammeId as Id<"keyStages"> };
    }
    if (!selectedCurriculumId) return "skip" as const;
    return { curriculumId: selectedCurriculumId as Id<"curriculum"> };
  }, [isIbCurriculum, selectedCurriculumId, selectedProgrammeId]);

  const { data: yearGroups = [] } = useQuery(
    convexQuery(
      api.parent.manageChildren.queries.getYearGroupsList,
      yearGroupsArgs,
    ),
  );

  const { data: subjects = [] } = useQuery(
    convexQuery(
      api.parent.manageChildren.queries.getSubjectsForYearGroup,
      selectedYearGroupId
        ? { yearGroupId: selectedYearGroupId as Id<"yearGroups"> }
        : "skip",
    ),
  );

  const { data: keyStage } = useQuery(
    convexQuery(
      api.parent.manageChildren.queries.getKeyStageForYearGroup,
      selectedYearGroupId
        ? { yearGroupId: selectedYearGroupId as Id<"yearGroups"> }
        : "skip",
    ),
  );

  const { mutateAsync: deleteChild, isPending: isDeletingChild } = useMutation({
    mutationFn: useConvexMutation(
      api.parent.manageChildren.mutations.deleteChild,
    ),
  });

  const { mutateAsync: resendInvite, isPending: isResendingInvite } =
    useMutation({
      mutationFn: useConvexMutation(
        api.parent.manageChildren.mutations.resendInvite,
      ),
    });

  const handleCurriculumOptionsOpen = useCallback(async () => {
    const currentCurriculumId = watch("curriculumId") as string;
    const result = await openSingleSelectSheet({
      options: curriculums,
      preSelectedOption: currentCurriculumId,
      title: "Curriculum system",
    });
    if (
      result?.selectedOption &&
      result.selectedOption !== currentCurriculumId
    ) {
      setValue("curriculumId", result.selectedOption as string, {
        shouldValidate: true,
      });
      setValue("programmeId", "", { shouldValidate: true });
      setValue("yearGroupId", "", { shouldValidate: true });
      setValue("subjects", [], { shouldValidate: true });
    }
  }, [openSingleSelectSheet, curriculums, watch, setValue]);

  const handleProgrammeOptionsOpen = useCallback(async () => {
    const currentProgrammeId = watch("programmeId") as string;
    const result = await openSingleSelectSheet({
      options: programmes,
      preSelectedOption: currentProgrammeId,
      title: "Which IB programme is your child currently in?",
    });
    if (
      result?.selectedOption &&
      result.selectedOption !== currentProgrammeId
    ) {
      setValue("programmeId", result.selectedOption as string, {
        shouldValidate: true,
      });
      setValue("yearGroupId", "", { shouldValidate: true });
      setValue("subjects", [], { shouldValidate: true });
    }
  }, [openSingleSelectSheet, programmes, watch, setValue]);

  const handleYearGroupOptionsOpen = useCallback(async () => {
    const currentYearGroupId = watch("yearGroupId") as string;
    const result = await openSingleSelectSheet({
      options: yearGroups,
      preSelectedOption: currentYearGroupId,
      title: isIbCurriculum
        ? "What grade is your child in?"
        : "Year Group",
    });
    if (
      result?.selectedOption &&
      result.selectedOption !== currentYearGroupId
    ) {
      setValue("yearGroupId", result.selectedOption as string);
      setValue("subjects", [], { shouldValidate: true });
    }
  }, [openSingleSelectSheet, yearGroups, watch, setValue, isIbCurriculum]);

  const isSaveEnabled = isValid && (!isIbCurriculum || !!selectedProgrammeId);

  const handleSubjectOptionsOpen = useCallback(async () => {
    const currentSubjects = watch("subjects") ?? [];
    const result = await openMultiSelectSheet({
      options: subjects,
      preSelectedOptions: currentSubjects.map((subject) => subject.subjectId),
    });
    if (result?.selectedOptions) {
      const selectedSubjects = result.selectedOptions.map((subjectId) => {
        const existing = currentSubjects.find(
          (subject) => subject.subjectId === subjectId,
        );
        return {
          subjectId,
          examDetails: existing?.examDetails,
          qualificationId: existing?.qualificationId,
        };
      });

      setValue("subjects", selectedSubjects, { shouldValidate: true });
    }
  }, [openMultiSelectSheet, subjects, watch, setValue]);
  const { mutateAsync: updateChild } = useMutation({
    mutationFn: useConvexMutation(
      api.parent.manageChildren.mutations.updateChild,
    ),
  });

  const { mutateAsync: createChild } = useMutation({
    mutationFn: useConvexMutation(
      api.parent.manageChildren.mutations.createChild,
    ),
    onError: (error) => {
      if (error instanceof ConvexError) {
        const { errors } = error.data;
        if (errors) {
          Object.entries(errors).forEach(([key, value]) => {
            setError(key as keyof ChildFormInput, { message: value as string });
          });
        }
      }
    },
  });

  const handleDeleteChild = useCallback(async () => {
    try {
      await deleteChild({ id: child?._id as Id<"students"> });

      setSelectedChildId(null);
    } catch (error) {
      const { message: errorMessage } = handleAuthError(error);

      Toast.show({
        type: "error",
        text1: errorMessage,
      });
    }
  }, [deleteChild, child?._id]);

  const onResendInvite = useCallback(async () => {
    try {
      await resendInvite({ studentId: child?._id as Id<"students"> });

      Toast.show({
        type: "success",
        text1: "Invite sent successfully",
        text1Style: {
          fontFamily: "Geologica_400Regular",
          fontSize: 10,
          color: "#000000",
        },
      });
    } catch (error) {
      const { message: errorMessage } = handleAuthError(error);

      Toast.show({
        type: "error",
        text1: errorMessage,
        text1Style: {
          fontFamily: "Geologica_400Regular",
          fontSize: 10,
          color: "#000000",
        },
      });
    }
  }, [resendInvite, child?._id]);

  const onSubmit = useCallback(
    async (data: ChildFormInput) => {
      try {
        if (createMode) {
          const childId = await createChild({
            firstName: data.firstName,
            email: data.email,
            keyStageId: keyStage?.value as Id<"keyStages">,
            yearGroupId: data.yearGroupId as Id<"yearGroups">,
            curriculumId: data.curriculumId as Id<"curriculum">,
            subjects: data.subjects as {
              subjectId: Id<"subjects">;
              qualificationId?: Id<"qualifications">;
              examDetails?: string;
            }[],
            learningPreferences: data.learningPreferences as (
              | "in_person"
              | "remote"
            )[],
            additionalLearningNeeds: data.additionalLearningNeeds,
            school: data.school,
          });

          setSelectedChildId(childId);
          setCreateMode(false);
        } else {
          await updateChild({
            id: child?._id as Id<"students">,
            firstName: data.firstName,
            email: data.email,
            keyStageId: keyStage?.value as Id<"keyStages">,
            yearGroupId: data.yearGroupId as Id<"yearGroups">,
            curriculumId: data.curriculumId as Id<"curriculum">,
            subjects: data.subjects as {
              subjectId: Id<"subjects">;
              qualificationId?: Id<"qualifications">;
              examDetails?: string;
            }[],
            learningPreferences: data.learningPreferences as (
              | "in_person"
              | "remote"
            )[],
            additionalLearningNeeds: data.additionalLearningNeeds,
            school: data.school,
          });
        }

        Toast.show({ type: "success", text1: "Saved" });
        onScrollToTop();
      } catch (error) {
        const { message: errorMessage } = handleAuthError(error);

        Toast.show({
          type: "error",
          text1: errorMessage,
          text1Style: {
            fontFamily: "Geologica_400Regular",
            fontSize: 10,
            color: "#000000",
          },
        });
      }
    },
    [
      createMode,
      createChild,
      keyStage?.value,
      setSelectedChildId,
      setCreateMode,
      updateChild,
      child?._id,
      onScrollToTop,
    ],
  );

  return (
    <View className="gap-8">
      <View className="flex-row items-center gap-2">
        <BasicInfoIcon pathClassName="stroke-black" />
        <Text className="font-geologica-medium font-medium text-xl text-black leading-[1.5]">
          Basic info
        </Text>
      </View>

      <View className="gap-4">
        <Controller
          control={control}
          name="firstName"
          render={({ field, fieldState }) => (
            <Input
              size="md"
              placeholder="First name"
              {...field}
              error={!!fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="email"
          render={({ field, fieldState }) => (
            <Fragment>
              <Input
                size="md"
                placeholder="Email"
                {...field}
                error={!!fieldState.error?.message}
                keyboardType="email-address"
                autoCapitalize="none"
                editable={createMode}
                disabled={!createMode}
              />

              {fieldState.error?.message ? (
                <Input.Message className="text-red-500 mt-1">
                  {fieldState.error.message}
                </Input.Message>
              ) : (
                <Input.Message className="mt-[-12px] text-xs">
                  Your child's email address must be different from yours
                </Input.Message>
              )}
            </Fragment>
          )}
        />

        {!createMode && child && !child.hasCompletedSignUp && (
          <View className="gap-3 px-4 py-2 flex-row items-center justify-between">
            <View className="flex-1">
              <Text className="font-geologica-regular text-xs text-black">
                Your child hasn't completed their account setup yet.
              </Text>

              <Text className="font-geologica-regular text-xs text-black">
                Resend the invite email to complete the setup.
              </Text>
            </View>

            <Button
              outline
              intent="primary"
              size="sm"
              icon
              className="border self-start"
              disabled={isResendingInvite}
              onPress={onResendInvite}
            >
              <Button.Icon as={SendMessageIcon} intent="primary" />

              <Button.Text size="sm" intent="primary">
                {isResendingInvite ? "Sending..." : "Resend invite"}
              </Button.Text>
            </Button>
          </View>
        )}

        <Controller
          control={control}
          name="school"
          render={({ field }) => (
            <Input
              size="md"
              placeholder="School (optional)"
              {...field}
              value={field.value ?? ""}
            />
          )}
        />

        <Controller
          control={control}
          name="curriculumId"
          render={({ field }) => (
            <Select size="md" onPress={handleCurriculumOptionsOpen}>
              <Select.Label size="md">
                {field.value
                  ? curriculums.find(
                      (curriculum) => curriculum.value === field.value,
                    )?.label
                  : "Curriculum system"}
              </Select.Label>
              <Select.DownArrow />
            </Select>
          )}
        />
        {isIbCurriculum ? (
          <Controller
            control={control}
            name="programmeId"
            render={({ field }) => (
              <Select size="md" onPress={handleProgrammeOptionsOpen}>
                <Select.Label size="md">
                  {field.value
                    ? programmes.find(
                        (programme) => programme.value === field.value,
                      )?.label
                    : "Which IB programme is your child currently in?"}
                </Select.Label>
                <Select.DownArrow />
              </Select>
            )}
          />
        ) : null}
        <Controller
          control={control}
          name="yearGroupId"
          render={({ field }) => (
            <Select
              size="md"
              onPress={handleYearGroupOptionsOpen}
              disabled={
                !selectedCurriculumId ||
                (isIbCurriculum && !selectedProgrammeId)
              }
            >
              <Select.Label size="md">
                {field.value
                  ? yearGroups.find(
                      (yearGroup) => yearGroup.value === field.value,
                    )?.label
                  : isIbCurriculum
                    ? "What grade is your child in?"
                    : "Year Group"}
              </Select.Label>
              <Select.DownArrow />
            </Select>
          )}
        />
        <View className="gap-4">
          <Select
            size="md"
            onPress={handleSubjectOptionsOpen}
            disabled={!watch("yearGroupId")}
          >
            <Select.Label>Interested subjects</Select.Label>
            <Select.DownArrow />
          </Select>
          <Select.Results
            selectedOptions={subjects.filter((option) =>
              (watch("subjects") ?? []).some(
                (subject) => subject.subjectId === option.value,
              ),
            )}
            onRemove={(value) => {
              setValue(
                "subjects",
                (watch("subjects") ?? []).filter(
                  (option) => option.subjectId !== value,
                ),
                { shouldValidate: true },
              );
            }}
          />
          <SubjectQualificationsForm
            control={control}
            subjects={watch("subjects") ?? []}
            yearGroupId={watch("yearGroupId") as string}
          />
        </View>
      </View>

      <View className="flex-row items-center gap-2">
        <LearningPreferencesIcon pathClassName="stroke-black" />
        <Text className="font-geologica-medium font-medium text-xl text-black leading-[1.5]">
          Learning preferences
        </Text>
      </View>

      <View className="gap-8">
        <View className="gap-4">
          <Text className="font-geologica-medium leading-[1.5] font-medium text-base text-black">
            Lesson mode
          </Text>

          <Controller
            control={control}
            name="learningPreferences"
            render={({ field }) => (
              <Fragment>
                <View className="gap-2">
                  <CheckboxInput onPress={() => togglePreference("in_person")}>
                    <CheckboxInput.Label size="md">
                      In-person
                    </CheckboxInput.Label>
                    <CheckboxInput.Icon
                      checked={field.value?.includes("in_person")}
                    />
                  </CheckboxInput>

                  <Text className="font-geologica-regular leading-[1.5] font-normal text-sm text-black">
                    In-person lessons take place at your house. Share your
                    address at the time of booking a lesson.
                  </Text>
                </View>

                <View className="gap-2">
                  <CheckboxInput onPress={() => togglePreference("remote")}>
                    <CheckboxInput.Label size="md">Remote</CheckboxInput.Label>
                    <CheckboxInput.Icon
                      checked={field.value?.includes("remote")}
                    />
                  </CheckboxInput>
                </View>
              </Fragment>
            )}
          />
        </View>

        <View className="gap-4">
          <Text className="font-geologica-medium leading-[1.5] font-medium text-base text-black">
            Additional learning needs
          </Text>

          <Controller
            control={control}
            name="additionalLearningNeeds"
            render={({ field }) => (
              <Textarea
                className="font-geologica-regular text-sm text-black font-normal py-2 px-3"
                placeholder="Please describe any additional learning needs, learning difficulties or additional support requirements (eg: autism, dyslexia, ADHD, etc.)"
                size="lg"
                multiline
                numberOfLines={4}
                textAlignVertical="top"
                {...field}
              />
            )}
          />

          <Text className="font-geologica-regular leading-[1.5] font-normal text-sm text-black">
            Contact admin if you need to discuss your child&apos;s learning
            needs?
          </Text>

          <View className="mx-[-12]">
            <Button borderLess size="sm" icon onPress={contactSupport}>
              <Button.Icon as={ContactIcon} intent="primary" />
              <Button.Text size="sm" intent="primary">
                Contact admin
              </Button.Text>
            </Button>
          </View>
        </View>
      </View>

      {!createMode && (
        <View className="gap-6">
          <Text className="font-geologica-medium font-medium text-xl text-black leading-[1.5]">
            Delete Profile
          </Text>
          <View className="gap-2">
            <Button
              outline
              disabled={!child?._id || isDeletingChild}
              intent="danger"
              size="sm"
              icon
              className="border"
              onPress={() => setIsDeleteDialogOpen(true)}
            >
              <Button.Icon as={CloseIcon} intent="danger" />

              <Button.Text size="sm" intent="danger">
                {isDeletingChild ? "Deleting..." : "Delete Profile"}
              </Button.Text>
            </Button>

            <Text className="font-geologica-regular leading-[1.5] font-normal text-sm text-black">
              This action cannot be undone.
            </Text>
          </View>
        </View>
      )}

      <View className="mt-4 mb-8 items-end">
        <Button
          intent="primary"
          size="sm"
          className="px-8"
          disabled={!isSaveEnabled || !keyStage?.value}
          onPress={handleSubmit(onSubmit)}
        >
          <Button.Text size="sm">Save</Button.Text>
        </Button>
      </View>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete child's profile</AlertDialogTitle>

            <AlertDialogDescription className="text-red-600 font-geologica-regular text-sm">
              You are about to delete your child's profile, this will erase all
              data associated with it, are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>

            <AlertDialogAction
              variant="destructive"
              onPress={handleDeleteChild}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </View>
  );
}

export default ChildForm;
